import React from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { 
  PageContainer, 
  ContentContainer, 
  Text, 
  Card, 
  Theme 
} from '../../components/ui';
import { Ionicons } from '@expo/vector-icons';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';

const AboutMentoristScreen: React.FC = () => {
  useNavigationLogging('AboutMentoristScreen');
  const navigation = useNavigation();

  return (
    <PageContainer>
      <ContentContainer>
        {/* Header */}
        <View style={styles.header}>
          <Pressable 
            onPress={() => navigation.goBack()} 
            style={styles.backButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="chevron-back" size={24} color={Theme.colors.textPrimary} />
          </Pressable>
          <Text variant="heading2" style={styles.headerTitle}>
            About Mentorist
          </Text>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Mission Statement */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Our Mission
            </Text>
            <Text variant="body1" style={styles.bodyText}>
              Mentorist is on a mission to turn book knowledge into real-world change. We help ambitious people make consistent progress on the goals that matter most—in just a few minutes a day.
            </Text>
          </Card>

          {/* The Problem We Solve */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Why Mentorist Exists
            </Text>
            <Text variant="body1" style={styles.bodyText}>
              People read books, listen to podcasts, and take notes—but still feel stuck. The real gap isn't knowledge—it's doing.
            </Text>
            <Text variant="body1" style={styles.bodyTextSpaced}>
              You might know what would help, but keep stalling when life gets busy. Or feel overwhelmed by too many books and conflicting advice. Or lose momentum after the initial burst of motivation.
            </Text>
            <Text variant="body1" style={styles.bodyText}>
              As a result, shelves are full, Kindle libraries overflow, yet careers, habits, and relationships often look the same.
            </Text>
          </Card>

          {/* How Mentorist Helps */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.sectionTitle}>
              How Mentorist Helps
            </Text>
            <Text variant="body1" style={styles.bodyTextSpaced}>
              Mentorist closes the gap between knowing and doing by combining three things in one app:
            </Text>
            
            <View style={styles.featureItem}>
              <View style={styles.iconCircle}>
                <Ionicons name="person" size={20} color={Theme.colors.primary} />
              </View>
              <View style={styles.featureContent}>
                <Text variant="body1" style={styles.featureTitle}>
                  Ultra-Personalized Setup
                </Text>
                <Text variant="body2" color="secondary">
                  A 2-minute onboarding that understands what you're stuck on, your goals, and your preferences.
                </Text>
              </View>
            </View>

            <View style={styles.featureItem}>
              <View style={styles.iconCircle}>
                <Ionicons name="chatbubbles" size={20} color={Theme.colors.primary} />
              </View>
              <View style={styles.featureContent}>
                <Text variant="body1" style={styles.featureTitle}>
                  AI Mentor You Can Talk To Anytime
                </Text>
                <Text variant="body2" color="secondary">
                  A smart, book-grounded mentor that remembers your context and your last commitment.
                </Text>
              </View>
            </View>

            <View style={styles.featureItem}>
              <View style={styles.iconCircle}>
                <Ionicons name="footsteps" size={20} color={Theme.colors.primary} />
              </View>
              <View style={styles.featureContent}>
                <Text variant="body1" style={styles.featureTitle}>
                  Daily Steps from Great Books
                </Text>
                <Text variant="body2" color="secondary">
                  One doable step you can apply immediately, with follow-up to keep you moving forward.
                </Text>
              </View>
            </View>
          </Card>

          {/* What Makes Us Different */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.sectionTitle}>
              What Makes Mentorist Different
            </Text>
            
            <View style={styles.differentiatorItem}>
              <Ionicons name="checkmark-circle" size={20} color={Theme.colors.success} style={styles.checkmark} />
              <Text variant="body1" style={styles.differentiatorText}>
                <Text style={styles.boldText}>Action-first, not information-first</Text> – Every insight is tied to concrete steps you can take today.
              </Text>
            </View>

            <View style={styles.differentiatorItem}>
              <Ionicons name="checkmark-circle" size={20} color={Theme.colors.success} style={styles.checkmark} />
              <Text variant="body1" style={styles.differentiatorText}>
                <Text style={styles.boldText}>Built to beat procrastination</Text> – Tiny steps with follow-up and fallback options keep you moving.
              </Text>
            </View>

            <View style={styles.differentiatorItem}>
              <Ionicons name="checkmark-circle" size={20} color={Theme.colors.success} style={styles.checkmark} />
              <Text variant="body1" style={styles.differentiatorText}>
                <Text style={styles.boldText}>Rooted in real books</Text> – Advice is grounded in proven frameworks and respected authors.
              </Text>
            </View>

            <View style={styles.differentiatorItem}>
              <Ionicons name="checkmark-circle" size={20} color={Theme.colors.success} style={styles.checkmark} />
              <Text variant="body1" style={styles.differentiatorText}>
                <Text style={styles.boldText}>Built for everyday life</Text> – Short, flexible content that fits into busy schedules.
              </Text>
            </View>

            <View style={styles.differentiatorItem}>
              <Ionicons name="checkmark-circle" size={20} color={Theme.colors.success} style={styles.checkmark} />
              <Text variant="body1" style={styles.differentiatorText}>
                <Text style={styles.boldText}>Supportive mentor presence</Text> – You're never alone with your self-improvement.
              </Text>
            </View>
          </Card>

          {/* Quote */}
          <Card style={[styles.card, styles.quoteCard]}>
            <Ionicons name="quote" size={32} color={Theme.colors.primary} style={styles.quoteIcon} />
            <Text variant="body1" style={styles.quoteText}>
              "Instead of asking, 'What should I read next?' you can open Mentorist and see, 'Here's the best thing you can do today to move forward.'"
            </Text>
          </Card>

          {/* The Result */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.sectionTitle}>
              The Result
            </Text>
            <Text variant="body1" style={styles.bodyText}>
              This daily focus is what turns books into habits and ideas into outcomes. Over time, this rhythm builds confidence, clarity, and visible growth—without requiring hours of extra reading.
            </Text>
          </Card>

          <View style={styles.bottomSpacer} />
        </ScrollView>
      </ContentContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: Theme.spacing.md,
    marginBottom: Theme.spacing.lg,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    // Empty - just for structure
  },
  card: {
    marginBottom: Theme.spacing.lg,
    padding: Theme.spacing.lg,
  },
  sectionTitle: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    marginBottom: Theme.spacing.md,
    color: Theme.colors.textPrimary,
  },
  bodyText: {
    color: Theme.colors.textPrimary,
    lineHeight: 24,
  },
  bodyTextSpaced: {
    color: Theme.colors.textPrimary,
    lineHeight: 24,
    marginTop: Theme.spacing.md,
  },
  featureItem: {
    flexDirection: 'row',
    marginBottom: Theme.spacing.lg,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Theme.colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: Theme.spacing.md,
  },
  featureContent: {
    flex: 1,
    paddingTop: 2,
  },
  featureTitle: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    marginBottom: Theme.spacing.xs,
    color: Theme.colors.textPrimary,
  },
  differentiatorItem: {
    flexDirection: 'row',
    marginBottom: Theme.spacing.md,
  },
  checkmark: {
    marginRight: Theme.spacing.sm,
    marginTop: 2,
  },
  differentiatorText: {
    flex: 1,
    color: Theme.colors.textPrimary,
    lineHeight: 24,
  },
  boldText: {
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  quoteCard: {
    backgroundColor: Theme.colors.primaryLight,
  },
  quoteIcon: {
    marginBottom: Theme.spacing.md,
    opacity: 0.6,
  },
  quoteText: {
    fontStyle: 'italic',
    color: Theme.colors.textPrimary,
    lineHeight: 24,
  },
  bottomSpacer: {
    height: Theme.spacing.xxxl,
  },
});

export default AboutMentoristScreen;





